/**
 * "Test on one case" when the dataset's outputs are the runner: score one case
 * with the chosen evaluators (and this experiment's mappings), as the run
 * would, without calling an agent. The case is an item with an output, or —
 * for a live dataset — one recent trace its source matches. Nothing is saved;
 * judges cost as usual. The picked case shows first: input left, output right.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Select } from "@/components/ui/select";
import { errorText } from "@/components/ui/states";
import { useDatasetItems, useDatasetSource } from "@/features/datasets/hooks";
import { SourceAuthError } from "@/features/settings/reconnect";
import type { DatasetItem } from "@/features/datasets/types";
import { useRecentTraces } from "@/features/traces/hooks";
import { refKey } from "@/features/traces/observation-table";
import { isAuthError, useSourceKind } from "@/features/traces/sources";
import { fromFilters } from "@/features/traces/trace-query";
import { formatDateTime } from "@/lib/format";
import { itemLabel } from "@/lib/item-label";
import { useApiMutation } from "@/lib/query";
import type { components } from "@/lib/api-schema";
import type { NewExperiment } from "./use-new-experiment";
import { ItemPreview, TracePreview } from "./case-preview";
import { CaseScores } from "./case-scores";
import { evaluatorChoices } from "./new-experiment-payload";

type ScoreOneIn = components["schemas"]["ScoreOneIn"];
type ScoredCase = components["schemas"]["ScoredCaseOut"];
type Case = {
  value: string;
  label: string;
  body: Pick<ScoreOneIn, "dataset_item_id" | "trace">;
  item?: DatasetItem;
};

/**
 * The cases to pick from: items with an output, or a live dataset's recent
 * traces, and the trace source's own error when it refused to list them.
 */
function useCases(flow: NewExperiment): { cases: Case[]; refused: string | null } {
  const items = useDatasetItems(flow.datasetId ?? "", flow.live ? null : flow.version, 1).data?.items ?? [];
  const source = useDatasetSource(flow.datasetId ?? "", flow.live).data;
  const recent = useRecentTraces(source?.connection_id ?? "", fromFilters(source?.filters));
  const traces = recent.data?.items ?? [];
  if (flow.live && source)
    return {
      refused: isAuthError(recent.error) ? source.connection_id : null,
      cases: traces.map((t) => ({
        value: refKey(t),
        label: `${t.name ?? t.id.slice(0, 8)} · ${formatDateTime(t.timestamp)}`,
        body: {
          trace: { connection_id: source.connection_id, trace_id: t.id, observation_id: t.observation_id },
        },
      })),
    };
  const cases = items
    .filter((i) => i.output !== null && i.output !== undefined)
    .map((i) => ({
      value: i.id,
      label: itemLabel(i.position, i.input),
      body: { dataset_item_id: i.id },
      item: i,
    }));
  return { cases, refused: null };
}

export function ScoreOneCase({ flow }: { flow: NewExperiment }) {
  const { cases, refused } = useCases(flow);
  const sourceLabel = useSourceKind(refused).label;
  const [picked, setPicked] = useState<string | null>(null);
  const chosen = cases.find((c) => c.value === picked) ?? cases[0];
  const score = useApiMutation<ScoredCase, ScoreOneIn>("post", "/experiments/score-one");
  const evaluators = evaluatorChoices(flow);
  return (
    <fieldset className="flex flex-col gap-3 rounded-[9px] border border-line p-3.5">
      <legend className="px-1 text-[13px] font-semibold">Test on one case</legend>
      <p className="-mt-1 text-xs text-muted">
        Scores one case with the chosen evaluators, as the experiment would. Nothing is saved.
      </p>
      {refused && <SourceAuthError connectionId={refused} label={sourceLabel} />}
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Field label="Case" htmlFor="score-case">
            <Select
              id="score-case"
              value={chosen?.value ?? ""}
              onChange={(v) => {
                setPicked(v);
                score.reset();
              }}
              placeholder={
                refused
                  ? `${sourceLabel} refused the sign-in`
                  : flow.live
                    ? "No recent traces match"
                    : "No items with an output"
              }
              options={cases.map(({ value, label }) => ({ value, label }))}
            />
          </Field>
        </div>
        <Button
          disabled={!chosen || evaluators.length === 0 || score.isPending}
          onClick={() => chosen && score.mutate({ evaluators, ...chosen.body })}
        >
          {score.isPending ? "Scoring…" : "Test on one case"}
        </Button>
      </div>
      {chosen?.item ? (
        <ItemPreview item={chosen.item} />
      ) : (
        chosen?.body.trace && <TracePreview trace={chosen.body.trace} />
      )}
      {score.isError && (
        <p role="alert" className="text-[13px] text-err">
          {errorText(score.error, "Could not score the case.")}
        </p>
      )}
      {score.data && <CaseScores scores={score.data.scores} label="Test result" />}
    </fieldset>
  );
}
